import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, CreditCard, Sparkles, ShieldCheck, BadgeCheck, Zap } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Spinner, VerifiedBadge } from "../components/common";

const PERKS = [
  { icon: BadgeCheck, title: "Blue verified badge", body: "A blue check by your Cash Tag so everyone knows it's really you — not a copycat." },
  { icon: ShieldCheck, title: "24/7 Account Guard", body: "AI watches your money, logins and devices around the clock and warns you fast." },
  { icon: Sparkles, title: "AI risk briefings", body: "A clear, plain-English safety report on your account, refreshed anytime." },
  { icon: Zap, title: "Get paid faster", body: "Verified accounts look professional and win trust before people pay you." },
];

export default function Badge() {
  const { refreshUser } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/plus/status"); setData(data); }
    catch (e) { toast.error(errMsg(e)); }
  };
  useEffect(() => { load(); }, []);

  const subscribe = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/plus/subscribe", { origin_url: window.location.origin });
      if (data.checkout_url) { window.location.href = data.checkout_url; return; }
    } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };

  const cancel = async () => {
    setBusy(true);
    try { const { data } = await api.post("/plus/cancel"); toast.success(data.message); await load(); await refreshUser(); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  if (!data) return <div className="py-16 grid place-items-center"><Spinner className="w-6 h-6 text-primary" /></div>;

  const sub = data.subscription;
  const isActive = data.subscribed;
  const canceled = sub && sub.cancel_at_period_end;
  const price = data.price_display || "$4.99";
  const trialDays = data.trial_days || 3;
  const until = sub?.current_period_end ? sub.current_period_end.slice(0, 10)
    : sub?.trial_end ? sub.trial_end.slice(0, 10) : "the end of the period";

  return (
    <div className="animate-fade-up" data-testid="plus-page">
      <PageHeader title="Jack Cash Plus" subtitle="Everything that makes you trusted & protected" />

      {isActive ? (
        <div data-testid="plus-status-card" className="rounded-3xl p-6 mb-5 text-white relative overflow-hidden"
          style={{ background: "linear-gradient(140deg,#2F80FF,#1559C0)" }}>
          <div className="absolute -right-6 -top-6 w-28 h-28 rounded-full bg-white/10" />
          <div className="relative">
            <div className="flex items-center gap-2">
              <VerifiedBadge color="blue" size={26} testid="plus-badge-preview" />
              <span className="font-display text-xl font-semibold">Jack Cash Plus is active</span>
            </div>
            <p className="text-sm text-white/90 mt-2">
              {price}/month · {sub?.status === "trialing" ? `free trial · charges ${until}` : canceled ? `ends ${until}` : `renews ${until}`}
            </p>
            <div className="mt-3 grid grid-cols-1 gap-1.5">
              <span className="inline-flex items-center gap-2 text-sm text-white/90"><Check size={15} /> Blue verified badge</span>
              <span className="inline-flex items-center gap-2 text-sm text-white/90"><Check size={15} /> 24/7 Account Guard protection</span>
            </div>
            {!canceled && (
              <button data-testid="plus-cancel-btn" onClick={cancel} disabled={busy}
                className="mt-4 h-10 px-5 rounded-xl bg-white/20 font-semibold text-sm">Cancel plan</button>
            )}
            {canceled && <p className="mt-4 text-xs text-white/80">Your plan is set to end on {until}. You'll keep Plus until then.</p>}
          </div>
        </div>
      ) : null}

      {isActive && (
        <button data-testid="plus-briefings-btn" onClick={() => nav("/app/guard")}
          className="w-full flex items-center gap-3 p-4 rounded-2xl bg-card border border-border mb-2 transition-transform duration-200 active:scale-[0.98]">
          <span className="grid place-items-center w-10 h-10 rounded-xl bg-primary/10 text-primary shrink-0"><ShieldCheck size={18} /></span>
          <span className="flex-1 text-left">
            <span className="block text-sm font-semibold">Account Guard briefings</span>
            <span className="block text-[11px] text-muted-foreground">See your 24/7 AI risk report</span>
          </span>
          <Sparkles size={16} className="text-primary" />
        </button>
      )}

      {!isActive && (
        <>
          {/* Persuasive hero */}
          <div className="rounded-3xl p-6 mb-4 text-white relative overflow-hidden" style={{ background: "linear-gradient(140deg,#6B3BF5,#2A1270)" }}>
            <div className="absolute -right-6 -top-6 w-32 h-32 rounded-full bg-white/10" />
            <div className="relative">
              <div className="inline-flex items-center gap-1.5 mb-2">
                <VerifiedBadge color="blue" size={20} />
                <span className="text-[11px] font-bold uppercase tracking-wide text-white/80">Jack Cash Plus</span>
              </div>
              <h2 className="font-display text-2xl font-semibold leading-snug">Look trusted. Stay protected. All for less than a coffee.</h2>
              <p className="text-sm text-white/85 mt-2 leading-relaxed">
                Get the blue verified check <strong>and</strong> 24/7 AI fraud protection in one simple plan. People pay verified accounts faster, and Account Guard watches your back every second — so you never worry about your money.
              </p>
              <div className="mt-4 inline-flex items-baseline gap-1 bg-white/15 rounded-full px-4 py-2">
                <span className="font-display text-2xl font-bold">{price}</span>
                <span className="text-white/80 text-sm">/ month</span>
              </div>
              {!data.trial_used && <p className="text-white/90 text-xs mt-2 font-semibold">{trialDays}-day free trial · cancel anytime · no charge today</p>}
            </div>
          </div>

          {/* Perks */}
          <div className="space-y-3 mb-5">
            {PERKS.map((p) => (
              <div key={p.title} className="flex items-start gap-3 rounded-2xl bg-card border border-border p-4">
                <span className="grid place-items-center w-10 h-10 rounded-xl bg-primary/10 text-primary shrink-0"><p.icon size={18} /></span>
                <div><div className="font-semibold text-sm">{p.title}</div><div className="text-xs text-muted-foreground">{p.body}</div></div>
              </div>
            ))}
          </div>

          {/* Value nudge */}
          <div className="rounded-2xl bg-success/10 border border-success/20 p-4 mb-5 text-center">
            <p className="text-sm font-semibold text-success">Two premium features, one price.</p>
            <p className="text-xs text-muted-foreground mt-1">Verified badge and Account Guard — together for just {price}/month.</p>
          </div>

          <button data-testid="plus-subscribe-btn" onClick={subscribe} disabled={busy}
            className="w-full h-14 rounded-2xl bg-primary text-primary-foreground font-semibold flex items-center justify-center gap-2 disabled:opacity-50">
            {busy ? <Spinner className="w-5 h-5" /> : <><CreditCard size={18} /> {data.trial_used ? `Subscribe · ${price}/mo` : `Start ${trialDays}-day free trial`}</>}
          </button>
          <p className="text-xs text-muted-foreground text-center flex items-center justify-center gap-1.5 mt-2">
            <Sparkles size={13} /> Paid securely by card via Stripe {data.trial_used ? "" : `· you won't be charged during the ${trialDays}-day trial`}
          </p>
        </>
      )}
    </div>
  );
}
